/**
 * The scenario's content status as the server reports it: COMPLETE vs
 * CONTENT_INCOMPLETE, whether the stored definition still validates, and
 * whether it can be taken as a graded test.
 */

import { MODALITY_LABELS, reasonLabel, type ProgressionAuthoringView } from './progressionTypes';

export function AuthoringStatus({ view }: { view: Pick<ProgressionAuthoringView, 'contentStatus' | 'validation' | 'gradable' | 'updatedAt'> }) {
  const complete = view.contentStatus === 'complete';
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="authoring-status">
      <span
        data-testid="content-status"
        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${complete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
      >
        {complete ? 'COMPLETE' : 'CONTENT_INCOMPLETE'}
      </span>
      {!view.validation.valid && (
        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800" data-testid="stored-invalid">
          Stored definition no longer validates
        </span>
      )}
      <span
        data-testid="gradable"
        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${view.gradable.testing ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}
      >
        {view.gradable.testing ? 'Gradable (Testing)' : 'Not gradable'}
      </span>
      {view.gradable.reason && <span className="text-xs text-gray-500">{view.gradable.reason}</span>}
      {view.gradable.modalities && (
        <span className="flex flex-wrap gap-1" data-testid="gradable-modalities">
          {view.gradable.modalities.map((m) => (
            <span
              key={m.modality}
              data-testid={`gradable-${m.modality}`}
              data-gradable={m.gradable ? 'true' : 'false'}
              title={m.gradable ? `Graded against ${m.rubric?.id}@${m.rubric?.version}` : reasonLabel(m.reason)}
              className={`rounded-full px-2 py-0.5 text-xs ${m.gradable ? 'bg-blue-50 text-blue-800' : 'bg-gray-50 text-gray-500 line-through'}`}
            >
              {MODALITY_LABELS[m.modality]}{m.gradable ? '' : ` — ${reasonLabel(m.reason)}`}
            </span>
          ))}
        </span>
      )}
      {view.updatedAt && <span className="text-xs text-gray-400">Last saved {new Date(view.updatedAt).toLocaleString()}</span>}
    </div>
  );
}
